import { useEffect, useState } from "react";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { apiGetJson } from "@/lib/sm-api";
import { AdminGlassPanel } from "./AdminGlassPanel";
import type { OnboardingPaymentAttempt } from "@/lib/onboarding-payment-attempts";
import type { SaasSelfServiceConfig } from "@/lib/saas-billing-config";

interface PaywayStatus {
  configured: boolean;
  environment: string;
  siteId: string | null;
  health: { ok: boolean; detail?: string };
  uala?: {
    configured: boolean;
    environment: string | null;
    health: { ok: boolean; detail?: string; environment?: string };
  };
  selfService?: SaasSelfServiceConfig | null;
  recentEvents: Array<{
    id: string;
    orderId: string | null;
    eventType: string;
    paymentId: string | null;
    amountCents: number | null;
    status: string | null;
    createdAt: string;
    provider?: string | null;
  }>;
  paymentAttempts?: OnboardingPaymentAttempt[];
  onboarding?: {
    byProvider: Array<{ provider: string; status: string; count: number }>;
    byStatus: Array<{ status: string; count: number }>;
    attemptsByProvider: Array<{ provider: string; status: string; count: number }>;
  };
  platform?: {
    volumeByProvider: Array<{ provider: string; status: string; count: number; cents: number }>;
    tenantConfigs: Array<{ provider: string; environment: string; tenants: number }>;
    requestTotals: Array<{ status: string; count: number; cents: number }>;
    webhooks: Record<string, number>;
  };
}

function fmt(cents: number | null, currency = "ARS") {
  if (cents == null) return "—";
  return new Intl.NumberFormat("es-AR", { style: "currency", currency }).format(cents / 100);
}

function GatewayCard({
  name,
  configured,
  environment,
  healthOk,
  healthDetail,
}: {
  name: string;
  configured: boolean;
  environment: string;
  healthOk: boolean;
  healthDetail?: string;
}) {
  return (
    <div className="flex items-center gap-2 rounded-lg border border-border/60 bg-secondary/15 p-3 min-w-[140px]">
      {configured ? (
        <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />
      ) : (
        <XCircle className="h-4 w-4 text-muted-foreground shrink-0" />
      )}
      <div>
        <p className="text-sm font-medium">{name}</p>
        <p className="text-xs text-muted-foreground">{configured ? environment : "no configurado"}</p>
        {configured ? (
          <p className={`text-xs ${healthOk ? "text-emerald-600" : "text-red-500"}`}>
            {healthOk ? "ok" : healthDetail ?? "error"}
          </p>
        ) : null}
      </div>
    </div>
  );
}

export function PaywayAdminPanel({ focus = "all" }: { focus?: "payway" | "uala" | "all" } = {}) {
  const [data, setData] = useState<PaywayStatus | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void apiGetJson<PaywayStatus>("/api/admin/payway")
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="glass-card flex items-center gap-2 rounded-xl p-4 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Cargando pagos…
      </div>
    );
  }
  if (!data) return null;

  const plat = data.platform;
  const ss = data.selfService;
  const attempts = data.paymentAttempts ?? [];

  return (
    <AdminGlassPanel
      title="Pagos — plataforma"
      description="Payway, Ualá Bis, self-service onboarding e intentos de pago (30 días)."
    >
      {/* Self-service status */}
      {ss ? (
        <div className="rounded-lg border border-border/60 bg-secondary/10 p-3 space-y-2">
          <p className="text-sm font-medium">Alta online (self-service)</p>
          <div className="flex flex-wrap gap-2">
            <Badge variant={ss.enabled ? "default" : "destructive"}>
              {ss.enabled ? "Habilitado" : "Deshabilitado"}
            </Badge>
            {ss.gateways.payway ? <Badge variant="outline">Payway</Badge> : null}
            {ss.gateways.uala ? <Badge variant="outline">Ualá</Badge> : null}
            {ss.gateways.manualTransferUsd ? <Badge variant="outline">Transfer USD</Badge> : null}
            {ss.gateways.manualTransferArs ? <Badge variant="outline">Transfer ARS</Badge> : null}
          </div>
          <p className="text-xs text-muted-foreground">
            Editá pasarelas y datos bancarios en la sección «Configuración self-service» más abajo.
          </p>
        </div>
      ) : null}

      {/* Gateways */}
      <div className="flex flex-wrap gap-3">
        {(focus === "all" || focus === "payway") && (
          <GatewayCard
            name="Payway"
            configured={data.configured}
            environment={data.environment}
            healthOk={data.health.ok}
            healthDetail={data.health.detail}
          />
        )}
        {(focus === "all" || focus === "uala") && (
          <GatewayCard
            name="Ualá Bis"
            configured={data.uala?.configured ?? false}
            environment={data.uala?.environment ?? data.uala?.health?.environment ?? "—"}
            healthOk={data.uala?.health?.ok ?? false}
            healthDetail={data.uala?.health?.detail}
          />
        )}
      </div>

      {/* Onboarding stats */}
      {data.onboarding ? (
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <p className="mb-2 text-sm font-medium">Sesiones onboarding por pasarela</p>
            {data.onboarding.byProvider.length === 0 ? (
              <p className="text-sm text-muted-foreground">Sin sesiones con pago.</p>
            ) : (
              <div className="space-y-1">
                {data.onboarding.byProvider.map((v) => (
                  <div key={`${v.provider}-${v.status}`} className="flex justify-between rounded-md border border-border/40 bg-secondary/10 px-2 py-1 text-xs">
                    <span>{v.provider} · {v.status}</span>
                    <span>{v.count}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div>
            <p className="mb-2 text-sm font-medium">Intentos de pago por pasarela</p>
            {data.onboarding.attemptsByProvider.length === 0 ? (
              <p className="text-sm text-muted-foreground">Sin intentos.</p>
            ) : (
              <div className="space-y-1">
                {data.onboarding.attemptsByProvider.map((v) => (
                  <div key={`${v.provider}-${v.status}`} className="flex justify-between rounded-md border border-border/40 bg-secondary/10 px-2 py-1 text-xs">
                    <span>{v.provider} · {v.status}</span>
                    <span>{v.count}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : null}

      {plat ? (
        <>
          <div>
            <p className="mb-2 text-sm font-medium">Volumen por proveedor (30 días)</p>
            {plat.volumeByProvider.length === 0 ? (
              <p className="text-sm text-muted-foreground">Sin eventos.</p>
            ) : (
              <div className="grid gap-1 sm:grid-cols-2">
                {plat.volumeByProvider.map((v) => (
                  <div
                    key={`${v.provider}-${v.status}`}
                    className="flex justify-between rounded-md border border-border/40 bg-secondary/10 px-2 py-1 text-xs"
                  >
                    <span>{v.provider} · {v.status || "—"}</span>
                    <span>{v.count} · {fmt(v.cents)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <p className="mb-2 text-sm font-medium">Tenants con provider activo</p>
            <div className="flex flex-wrap gap-2">
              {plat.tenantConfigs.length === 0 ? (
                <span className="text-xs text-muted-foreground">Ninguno</span>
              ) : (
                plat.tenantConfigs.map((c) => (
                  <Badge key={`${c.provider}-${c.environment}`} variant="outline">
                    {c.provider} ({c.environment}): {c.tenants}
                  </Badge>
                ))
              )}
            </div>
          </div>

          <div>
            <p className="mb-2 text-sm font-medium">Webhooks (30 días)</p>
            <div className="flex gap-4 text-sm">
              <span>webhook: {plat.webhooks.webhook ?? 0}</span>
              <span>duplicate: {plat.webhooks.duplicate ?? 0}</span>
              <span>error: {plat.webhooks.error ?? 0}</span>
            </div>
          </div>
        </>
      ) : null}

      <div>
        <p className="mb-2 text-sm font-medium">Últimos intentos de pago (onboarding)</p>
        {attempts.length === 0 ? (
          <p className="text-sm text-muted-foreground">Sin intentos todavía.</p>
        ) : (
          <div className="max-h-72 space-y-1 overflow-auto rounded-lg border border-border/60">
            {attempts.map((a) => (
              <div
                key={a.id}
                className="flex flex-wrap items-center justify-between gap-2 border-b border-border/40 px-2 py-1 text-xs last:border-0"
              >
                <span className="font-mono">
                  {a.provider} · {a.status}
                  {a.externalId ? ` · ${a.externalId.slice(0, 12)}` : ""}
                </span>
                <span className="flex items-center gap-2">
                  <span>{fmt(a.amountCents, a.currency ?? "ARS")}</span>
                  <span className="text-muted-foreground">{new Date(a.createdAt).toLocaleString("es-AR")}</span>
                  <Badge
                    variant={
                      a.status === "approved" ? "secondary" : a.status === "failed" ? "destructive" : "outline"
                    }
                  >
                    {a.status}
                  </Badge>
                </span>
                {a.errorMessage ? (
                  <span className="w-full text-red-600 truncate">{a.errorMessage}</span>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <p className="mb-2 text-sm font-medium">Últimos eventos (tenants)</p>
        {data.recentEvents.length === 0 ? (
          <p className="text-sm text-muted-foreground">Sin eventos todavía.</p>
        ) : (
          <div className="max-h-48 space-y-1 overflow-auto rounded-lg border border-border/60">
            {data.recentEvents.map((e) => (
              <div
                key={e.id}
                className="flex items-center justify-between border-b border-border/40 px-2 py-1 text-xs last:border-0"
              >
                <span className="font-mono">
                  {e.eventType}
                  {e.paymentId ? ` · ${e.paymentId}` : ""}
                </span>
                <span className="flex items-center gap-2">
                  <span>{fmt(e.amountCents)}</span>
                  <span className="text-muted-foreground">{new Date(e.createdAt).toLocaleString("es-AR")}</span>
                  {e.status ? (
                    <Badge
                      variant={
                        e.status === "approved" ? "secondary" : e.status === "rejected" ? "destructive" : "outline"
                      }
                    >
                      {e.status}
                    </Badge>
                  ) : null}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminGlassPanel>
  );
}
